//src/components/ChromaGrid.js
import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import StarBorder from './StarBorder';
import "./ChromaGrid.css";

export const ChromaGrid = ({
  items,
  className = "",
  radius = 300,
  columns = 4,
  rows = 2,
  damping = 0.45,
  fadeOut = 0.6,
  ease = "power3.out",
  setPage,
  page,
  totalPages,
  enableSlideAnimation = false,
  isAnimating,
  setIsAnimating,
  setShowSwipeCue, // New prop
}) => {
  const rootRef = useRef(null);
  const nextPageRef = useRef(null);
  const fadeRef = useRef(null);
  const setX = useRef(null);
  const setY = useRef(null);
  const pos = useRef({ x: 0, y: 0 });
  const [touchStartX, setTouchStartX] = useState(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [hasSwiped, setHasSwiped] = useState(() => sessionStorage.getItem('hasSwiped') === 'true');
  const { ref: inViewRef, inView } = useInView({
    triggerOnce: false,
    threshold: 0.5,
    rootMargin: '100px',
  });

  // Debug inView state and items
  useEffect(() => {
    console.log(
      'ChromaGrid inView:', inView,
      'itemsLength:', items.length,
      'currentPage:', page,
      'totalPages:', totalPages,
      'items:', items
    );
  }, [inView, items, page, totalPages]);

  // Preload images
  useEffect(() => {
    items.forEach(item => {
      const img = new Image();
      img.src = item.image;
    });
  }, [items]);

  // Combine refs for root element
  useEffect(() => {
    const setRef = (element) => {
      rootRef.current = element;
      inViewRef(element);
    };
    if (rootRef.current) {
      setRef(rootRef.current);
    }
  }, [inViewRef]);

  // Update showSwipeCue on swipe
  useEffect(() => {
    if (hasSwiped) {
      setShowSwipeCue?.(false);
    }
  }, [hasSwiped, setShowSwipeCue]);

  const prevPageRef = useRef(page);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    setX.current = gsap.quickSetter(el, "--x", "px");
    setY.current = gsap.quickSetter(el, "--y", "px");
    const { width, height } = el.getBoundingClientRect();
    pos.current = { x: width / 2, y: height / 2 };
    setX.current(pos.current.x);
    setY.current(pos.current.y);

    // Smooth slide-in animation on page change
    if (enableSlideAnimation && page !== prevPageRef.current) {
      gsap.set(el, { x: window.innerWidth * (page > prevPageRef.current ? 1 : -1), opacity: 0, willChange: 'transform, opacity' });
      gsap.to(el, {
        x: 0,
        opacity: 1,
        duration: 0.2,
        ease: "power4.inOut",
        onComplete: () => {
          prevPageRef.current = page;
        },
      });
    }
  }, [page, enableSlideAnimation]);

  const moveTo = (x, y) => {
    gsap.to(pos.current, {
      x,
      y,
      duration: damping,
      ease,
      onUpdate: () => {
        setX.current?.(pos.current.x);
        setY.current?.(pos.current.y);
      },
      overwrite: true,
    });
  };

  const handleMove = (e) => {
    const r = rootRef.current.getBoundingClientRect();
    moveTo(e.clientX - r.left, e.clientY - r.top);
    gsap.to(fadeRef.current, { opacity: 0, duration: 0 });
  };

  const handleLeave = () => {
    gsap.to(fadeRef.current, { opacity: 1, duration: 0 });
  };

  const handleCardMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    card.style.setProperty("--mouse-x", `${x}px`);
    card.style.setProperty("--mouse-y", `${y}px`);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setTouchStartX(e.touches[0].clientX);
      setIsSwiping(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isSwiping || !touchStartX || e.touches.length !== 1 || isAnimating) return;
    const currentX = e.touches[0].clientX;
    const deltaX = currentX - touchStartX;
    if (enableSlideAnimation) {
      gsap.set(rootRef.current, { x: deltaX, willChange: 'transform' });
      if (nextPageRef.current) {
        gsap.set(nextPageRef.current, { x: deltaX + (deltaX > 0 ? window.innerWidth : -window.innerWidth), willChange: 'transform' });
        console.log('NextPageX:', deltaX + (deltaX > 0 ? window.innerWidth : -window.innerWidth));
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (!isSwiping || !touchStartX || isAnimating) return;
    const touchEndX = e.changedTouches[0].clientX;
    const swipeDistance = touchStartX - touchEndX;
    const swipeThreshold = 50;

    console.log('Swipe: currentX:', swipeDistance, 'slideOutX:', swipeDistance > 0 ? -window.innerWidth : window.innerWidth);

    if (Math.abs(swipeDistance) > swipeThreshold && enableSlideAnimation) {
      setIsAnimating(true);
      const slideOutX = swipeDistance > 0 ? -window.innerWidth : window.innerWidth;
      const slideInX = swipeDistance > 0 ? window.innerWidth : -window.innerWidth;
      const newPage = swipeDistance > 0
        ? page === totalPages ? 1 : page + 1
        : page === 1 ? totalPages : page - 1;

      gsap.to(rootRef.current, {
        x: slideOutX,
        opacity: 0,
        duration: 0.1,
        ease: "power4.inOut",
        onComplete: () => {
          console.log('Before setPage:', { page, newPage, itemsLength: items.length });
          setPage(newPage);
          console.log('After setPage:', { page, newPage, itemsLength: items.length });
          setIsAnimating(false);
          if (!hasSwiped) {
            setHasSwiped(true);
            sessionStorage.setItem('hasSwiped', 'true');
            console.log('Setting showSwipeCue to false after swipe');
            setShowSwipeCue?.(false);
          }
        },
      });

      if (nextPageRef.current) {
        gsap.fromTo(
          nextPageRef.current,
          { x: slideInX, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.1, ease: "power4.inOut" }
        );
      }
    } else if (Math.abs(swipeDistance) > swipeThreshold) {
      const newPage = swipeDistance > 0
        ? page === totalPages ? 1 : page + 1
        : page === 1 ? totalPages : page - 1;
      setPage(newPage);
      if (!hasSwiped) {
        setHasSwiped(true);
        sessionStorage.setItem('hasSwiped', 'true');
        setShowSwipeCue?.(false);
      }
    }

    setTouchStartX(null);
    setIsSwiping(false);
  };

  // Add touch event listeners with passive: false
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Use items directly from Home.js
  const currentPageItems = items;
  const nextPage = page === totalPages ? 1 : page + 1;
  const nextPageItems = items; // Preload next page (will be updated by Home.js)

  console.log('CurrentPageItems:', currentPageItems.length, 'NextPageItems:', nextPageItems.length, 'Items:', currentPageItems);

  return (
    <div className="swipe-container">
      <div
        ref={rootRef}
        className={`chroma-grid ${className} ${inView ? 'in-view' : ''}`}
        style={{ "--r": `${radius}px`, "--cols": columns, "--rows": rows }}
        onPointerMove={handleMove}
        onPointerLeave={handleLeave}
      >
        {currentPageItems.length > 0 ? currentPageItems.map((item) => (
          <article
            key={item.id}
            className="chroma-card"
            onMouseMove={handleCardMove}
            style={{
              "--card-border": item.borderColor,
              "--card-gradient": item.gradient,
            }}
          >
            <div className="chroma-img-wrapper">
              <img src={item.image} alt={item.businessName} loading="lazy" />
              {item.badge && <span className="chroma-badge">{item.badge}</span>}
            </div>
            <footer className="chroma-info">
              <h3 className="name">{item.businessName}</h3>
              <p className="role">{item.service}</p>
              <span className="location">{item.location}</span>
              <p className="description">{item.description}</p>
              <div className="chroma-buttons">
                <StarBorder
                  as={Link}
                  to={item.profileUrl}
                  color={item.is_promoted ? "#8b5cf6" : "#3b82f6"}
                  speed="5s"
                  thickness={1}
                  className="chroma-button view-profile"
                >
                  View Profile
                </StarBorder>
                <Link to={item.orderUrl} className="chroma-button order">
                  Order
                </Link>
              </div>
            </footer>
          </article>
        )) : (
          <div>No items available for page {page}</div>
        )}
        <div className="chroma-overlay" style={{ opacity: 0.2 }} />
        <div ref={fadeRef} className="chroma-fade" style={{ opacity: 0.3 }} />
      </div>
      <div
        ref={nextPageRef}
        className="chroma-grid next-page"
        style={{ "--r": `${radius}px`, "--cols": columns, "--rows": rows, position: 'absolute', top: 0, left: '100%', opacity: 0 }}
      >
        {nextPageItems.length > 0 ? nextPageItems.map((item) => (
          <article
            key={item.id}
            className="chroma-card"
            style={{
              "--card-border": item.borderColor,
              "--card-gradient": item.gradient,
            }}
          >
            <div className="chroma-img-wrapper">
              <img src={item.image} alt={item.businessName} loading="lazy" />
              {item.badge && <span className="chroma-badge">{item.badge}</span>}
            </div>
            <footer className="chroma-info">
              <h3 className="name">{item.businessName}</h3>
              <p className="role">{item.service}</p>
              <span className="location">{item.location}</span>
              <p className="description">{item.description}</p>
              <div className="chroma-buttons">
                <StarBorder
                  as={Link}
                  to={item.profileUrl}
                  color={item.is_promoted ? "#8b5cf6" : "#3b82f6"}
                  speed="5s"
                  thickness={1}
                  className="chroma-button view-profile"
                >
                  View Profile
                </StarBorder>
                <Link to={item.orderUrl} className="chroma-button order">
                  Order
                </Link>
              </div>
            </footer>
          </article>
        )) : (
          <div>No items available for page {nextPage}</div>
        )}
        <div className="chroma-overlay" style={{ opacity: 0.2 }} />
        <div className="chroma-fade" style={{ opacity: 0.3 }} />
      </div>
    </div>
  );
};

export default ChromaGrid;
